import { useEffect, useRef } from 'react';

// Per-page engagement tracking.
//
// Django injects data-trackurl / data-tracktoken on #root when a view should
// report engagement (paid workspaces only; never for bots or owner previews).
// The hook accrues seconds against whichever page is current while the tab is
// visible, and posts cumulative totals every FLUSH_MS and on tab hide / unload.
// Totals are cumulative (not deltas) so a retried or duplicated beacon can't
// double-count; the server keeps the max it has seen per page.

const TICK_MS = 1000;
const FLUSH_MS = 10000;
const MAX_GAP_S = 5; // a tick delayed by throttling never credits more than this

export const readTrackingConfig = (dataset, params) => ({
    url: dataset.trackurl || params.get('trackurl') || '',
    token: dataset.tracktoken || params.get('tracktoken') || '',
});

const roundTotals = (totals) => {
    const out = {};
    Object.keys(totals).forEach((page) => {
        out[page] = Math.round(totals[page] * 10) / 10;
    });
    return out;
};

export const usePageTracking = ({ tracking, currentPage, numPages }) => {
    const url = tracking && tracking.url;
    const token = tracking && tracking.token;
    const enabled = Boolean(url && token);

    const pageRef = useRef(currentPage);
    const numPagesRef = useRef(numPages);
    const totalsRef = useRef({});
    const dirtyRef = useRef(false);
    const lastTickRef = useRef(null);
    pageRef.current = currentPage;
    numPagesRef.current = numPages;

    useEffect(() => {
        if (!enabled) return undefined;

        const accrue = () => {
            const now = Date.now();
            const page = pageRef.current;
            if (
                lastTickRef.current != null &&
                document.visibilityState === 'visible' &&
                page &&
                page > 0
            ) {
                const delta = Math.min((now - lastTickRef.current) / 1000, MAX_GAP_S);
                totalsRef.current[page] = (totalsRef.current[page] || 0) + delta;
                dirtyRef.current = true;
            }
            lastTickRef.current = now;
        };

        const send = (unloading) => {
            accrue();
            if (!dirtyRef.current) return;
            dirtyRef.current = false;
            const body = JSON.stringify({
                token,
                num_pages: numPagesRef.current || null,
                pages: roundTotals(totalsRef.current),
            });
            // text/plain keeps the request "simple" so sendBeacon works everywhere.
            if (unloading && navigator.sendBeacon) {
                try {
                    navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
                    return;
                } catch (e) {
                    // fall through to fetch
                }
            }
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain' },
                body,
                keepalive: true,
                credentials: 'omit',
            }).catch(() => {});
        };

        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                send(true);
            } else {
                lastTickRef.current = Date.now();
            }
        };
        const onPageHide = () => send(true);

        lastTickRef.current = Date.now();
        const tick = setInterval(accrue, TICK_MS);
        const flush = setInterval(() => send(false), FLUSH_MS);
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', onPageHide);

        return () => {
            clearInterval(tick);
            clearInterval(flush);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', onPageHide);
            send(true);
        };
    }, [enabled, url, token]);
};
